import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';

const NAVY = '#1E3A6E';
const RAJ = 'Rajdhani, sans-serif';
const inputS = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.85rem', boxSizing: 'border-box' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// How a match reads in the link dropdown: the date first, since that is what
// an admin knows about a video, then the teams.
const matchLabel = (m) => {
  const d = new Date(m.match_date).toLocaleDateString('en-GB',
    { day: '2-digit', month: 'short', year: 'numeric' });
  const teams = (m.capt1_team && m.capt2_team)
    ? `${m.capt1_team} vs ${m.capt2_team}`
    : (m.capt1_name && m.capt2_name) ? `${m.capt1_name} vs ${m.capt2_name}` : '';
  return `${d}${teams ? ' — ' + teams : ''}${m.series_name ? ' · ' + m.series_name : ''}`;
};

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  // Fetched once here and passed down. Every edit row needs the same list, and
  // one request beats one per row.
  // ?past=true is what lifts the year filter. GET /matches defaults to the
  // CURRENT year when neither `year` nor `past` is given, so a plain call
  // returned 2026 only — useless for an archive going back to 2013.
  //
  // Two calls: everything played (all seasons) plus anything still upcoming,
  // since a video can be added for a match that has just happened but is still
  // dated today.
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get('/matches?past=true&limit=500').catch(() => ({ data: { matches: [] } })),
      api.get('/matches?upcoming=true&limit=50').catch(() => ({ data: { matches: [] } })),
    ]).then(([pastRes, upRes]) => {
      const seen = new Set();
      const all = [...(upRes.data.matches || []), ...(pastRes.data.matches || [])]
        .filter(m => (seen.has(m.id) ? false : seen.add(m.id)))
        .sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
      setMatches(all);
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/videos/admin/all')
      .then(r => setVideos(r.data.videos || []))
      .catch(() => setMsg('Failed to load'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '18px 16px 60px' }}>
      <h1 style={{ fontFamily: RAJ, fontWeight: 800, color: NAVY, fontSize: '1.5rem', marginBottom: 14 }}>🎥 Match Videos</h1>
      <AddForm onDone={() => { load(); }} setMsg={setMsg} matches={matches} />
      {msg && <div style={{ margin: '10px 0', fontSize: '0.85rem', color: msg.includes('✓') ? '#16a34a' : '#dc2626' }}>{msg}</div>}

      {loading ? <div style={{ color: '#94a3b8', padding: 20 }}>Loading…</div>
        : videos.length === 0 ? <div style={{ color: '#94a3b8', padding: 20 }}>No videos yet.</div>
        : (
          <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
            {videos.map(v => <VideoRow key={v.id} v={v} onChanged={load} setMsg={setMsg} matches={matches} />)}
          </div>
        )}
    </div>
  );
}

function AddForm({ onDone, setMsg, matches }) {
  const [f, setF] = useState({ youtube_url: '', title: '', video_date: '', category: 'match', match_id: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setF(s => ({ ...s, [k]: val }));

  // Linking used to mean pasting a raw UUID, which an admin could only get by
  // digging it out of a scorecard URL. The matches are right here, so offer
  // them by date and teams instead.

  // Typing a date narrows the list to that day, which is almost always the one
  // wanted; otherwise the whole list is there.
  const dayFiltered = f.video_date
    ? (matches || []).filter(m => String(m.match_date).slice(0, 10) === f.video_date)
    : matches;
  const options = dayFiltered.length ? dayFiltered : (matches || []);



  const submit = () => {
    if (!f.youtube_url || !f.title) { setMsg('URL and title are required'); return; }
    setSaving(true); setMsg(null);
    api.post('/videos/admin', { ...f, video_date: f.video_date || null, match_id: f.match_id || null })
      .then(() => { setF({ youtube_url: '', title: '', video_date: '', category: 'match', match_id: '' }); setMsg('Added ✓'); onDone(); })
      .catch(e => setMsg(e?.response?.data?.error || 'Failed to add'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
      <div style={{ fontFamily: RAJ, fontWeight: 800, color: NAVY, marginBottom: 10 }}>➕ Add a video</div>
      <div style={{ display: 'grid', gap: 8 }}>
        <input style={inputS} placeholder="YouTube URL (any format)" value={f.youtube_url} onChange={e => set('youtube_url', e.target.value)} />
        <input style={inputS} placeholder="Title" value={f.title} onChange={e => set('title', e.target.value)} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inputS, flex: 1 }} type="date" value={f.video_date} onChange={e => set('video_date', e.target.value)} />
          <select style={{ ...inputS, flex: 1 }} value={f.category} onChange={e => set('category', e.target.value)}>
            <option value="match">Match</option>
            <option value="event">Event / Other</option>
          </select>
        </div>
        <select style={inputS} value={f.match_id} onChange={e => set('match_id', e.target.value)}>
          <option value="">Not linked to a match (optional — linking shows result &amp; MOM badges)</option>
          {options.map(m => (
            <option key={m.id} value={m.id}>{matchLabel(m)}</option>
          ))}
        </select>
        {f.video_date && dayFiltered.length > 0 && (
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: -4 }}>
            Showing {dayFiltered.length} match{dayFiltered.length === 1 ? '' : 'es'} on {f.video_date}. Clear the date to see all.
          </div>
        )}
        {f.video_date && dayFiltered.length === 0 && matches.length > 0 && (
          <div style={{ fontSize: '0.72rem', color: '#b45309', marginTop: -4 }}>
            No match on {f.video_date} — showing all matches.
          </div>
        )}
        <button onClick={submit} disabled={saving}
          style={{ border: 'none', background: NAVY, color: '#fff', borderRadius: 8, padding: '9px 12px', cursor: 'pointer', fontFamily: RAJ, fontWeight: 700, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Adding…' : 'Add video'}
        </button>
      </div>
    </div>
  );
}

function VideoRow({ v, onChanged, setMsg, matches }) {
  const [editing, setEditing] = useState(false);
  const [e, setE] = useState({ title: v.title, video_date: v.video_date ? String(v.video_date).slice(0, 10) : '', category: v.category, match_id: v.match_id || '', youtube_url: v.youtube_url });
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setE(s => ({ ...s, [k]: val }));

  const save = () => {
    setBusy(true);
    api.patch(`/videos/admin/${v.id}`, { ...e, video_date: e.video_date || null, match_id: e.match_id || null })
      .then(() => { setEditing(false); setMsg('Saved ✓'); onChanged(); })
      .catch(err => setMsg(err?.response?.data?.error || 'Failed'))
      .finally(() => setBusy(false));
  };
  const del = () => { if (!window.confirm('Delete this video?')) return; setBusy(true); api.delete(`/videos/admin/${v.id}`).then(() => onChanged()).finally(() => setBusy(false)); };
  const togglePub = () => { setBusy(true); api.patch(`/videos/admin/${v.id}`, { is_published: !v.is_published }).then(() => onChanged()).finally(() => setBusy(false)); };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: v.is_published === false ? '#fffbeb' : '#fff', display: 'flex', gap: 12 }}>
      <img src={`https://img.youtube.com/vi/${v.video_id}/default.jpg`} alt="" style={{ width: 88, height: 66, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <div style={{ display: 'grid', gap: 6 }}>
            <input style={inputS} value={e.title} onChange={ev => set('title', ev.target.value)} />
            <div style={{ display: 'flex', gap: 6 }}>
              <input style={{ ...inputS, flex: 1 }} type="date" value={e.video_date} onChange={ev => set('video_date', ev.target.value)} />
              <select style={{ ...inputS, flex: 1 }} value={e.category} onChange={ev => set('category', ev.target.value)}>
                <option value="match">Match</option><option value="event">Event</option>
              </select>
            </div>
            <select style={inputS} value={e.match_id} onChange={ev => set('match_id', ev.target.value)}>
              <option value="">Not linked to a match</option>
              {(matches || []).map(m => (
                <option key={m.id} value={m.id}>{matchLabel(m)}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={save} disabled={busy} style={{ flex: 1, border: 'none', background: NAVY, color: '#fff', borderRadius: 6, padding: '7px', cursor: 'pointer', fontWeight: 700 }}>Save</button>
              <button onClick={() => setEditing(false)} style={{ flex: 1, border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '7px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontFamily: RAJ, fontWeight: 700, color: NAVY, fontSize: '0.95rem' }}>{v.title}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', margin: '3px 0 8px' }}>
              {fmtDate(v.video_date)} · {v.category === 'event' ? 'Event' : 'Match'}{v.match_id ? ' · 🔗 linked' : ''}{v.is_published === false ? ' · hidden' : ''}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => setEditing(true)} style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.78rem' }}>Edit</button>
              <button onClick={togglePub} disabled={busy} style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.78rem' }}>{v.is_published === false ? 'Publish' : 'Unpublish'}</button>
              <button onClick={del} disabled={busy} style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.78rem' }}>Delete</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
